import { useMemo } from 'react';

const toNumber = (value) => {
    // Number(null) e Number('') retornam 0; aqui ausência não é zero.
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * O backend retorna uma linha por unidade (SELECT DISTINCT ano/valor), então o
 * mesmo ano se repete várias vezes e os gráficos de evolução anual ficam com o
 * eixo duplicado. Este hook sintetiza os anos repetidos em uma única linha.
 *
 * Índices como IDCRAS/IDCREAS são notas por unidade (escala 0–5), portanto o
 * padrão é a média — somar produziria valores fora da escala do indicador.
 *
 * @param {Array}  data       linhas retornadas pelo backend
 * @param {Array}  valueKeys  campos numéricos a sintetizar
 * @param {Object} [options]  groupBy (padrão 'ano') e method ('avg' | 'sum')
 * @returns {Array} uma linha por ano, em ordem crescente, com `count` de origens
 */
export const useAnnualSynthesis = (
    data,
    valueKeys = [],
    { groupBy = 'ano', method = 'avg' } = {}
) => {
    const valueKeysDeps = valueKeys.join(',');

    return useMemo(() => {
        if (!Array.isArray(data)) {
            return [];
        }

        const groups = new Map();

        data.forEach((item) => {
            const group = item?.[groupBy];

            if (group === null || group === undefined || group === '') {
                return;
            }

            if (!groups.has(group)) {
                groups.set(group, {
                    item: { ...item },
                    count: 0,
                    totals: {},
                    occurrences: {}
                });
            }

            const accumulated = groups.get(group);
            accumulated.count += 1;

            valueKeys.forEach((key) => {
                const value = toNumber(item?.[key]);

                if (value === null) {
                    return;
                }

                accumulated.totals[key] = (accumulated.totals[key] ?? 0) + value;
                accumulated.occurrences[key] =
                    (accumulated.occurrences[key] ?? 0) + 1;
            });
        });

        return [...groups.values()]
            .map(({ item, count, totals, occurrences }) => {
                const synthesized = { ...item, count };

                valueKeys.forEach((key) => {
                    const total = totals[key];
                    const occurrence = occurrences[key];

                    if (!occurrence) {
                        synthesized[key] = null;
                        return;
                    }

                    synthesized[key] =
                        method === 'sum'
                            ? total
                            : Math.round((total / occurrence) * 100) / 100;
                });

                return synthesized;
            })
            .sort((a, b) => String(a[groupBy]).localeCompare(String(b[groupBy])));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, groupBy, method, valueKeysDeps]);
};
